import type {PendingCapableAttachment, PendingUpload, PendingUploadAttachment, PromptAttachment, UserImageAttachment} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {PendingUploadStatus, PromptAttachmentType} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import {getUniqueId} from '@Utils/string.util';

interface PendingUploadEntry {
  promise: Promise<unknown>;
  blobUrl: string;
  file: File;
}

const pendingUploadRegistry = new Map<string, Map<string, PendingUploadEntry>>();

export const generatePendingLocalId = (): string => {
  return `pending-${getUniqueId(8)}`;
};

export const registerPendingUpload = (promptBoxId: string, localId: string, blobUrl: string, promise: Promise<unknown>, file: File): void => {
  let entries = pendingUploadRegistry.get(promptBoxId);
  if (!entries) {
    entries = new Map();
    pendingUploadRegistry.set(promptBoxId, entries);
  }
  entries.set(localId, {promise, blobUrl, file});
};

export const updatePendingUploadPromise = (promptBoxId: string, localId: string, promise: Promise<unknown>): void => {
  const entry = pendingUploadRegistry.get(promptBoxId)?.get(localId);
  if (!entry) {
    return;
  }
  entry.promise = promise;
};

export const getPendingUploadFile = (promptBoxId: string, localId: string): File | undefined => {
  return pendingUploadRegistry.get(promptBoxId)?.get(localId)?.file;
};

export const clearPendingUpload = (promptBoxId: string, localId: string): void => {
  const entries = pendingUploadRegistry.get(promptBoxId);
  const entry = entries?.get(localId);
  if (!entries || !entry) {
    return;
  }
  URL.revokeObjectURL(entry.blobUrl);
  entries.delete(localId);
  if (entries.size === 0) {
    pendingUploadRegistry.delete(promptBoxId);
  }
};

export const awaitPendingUploadsForPromptBox = async (promptBoxId: string): Promise<void> => {
  const entries = pendingUploadRegistry.get(promptBoxId);
  if (!entries || entries.size === 0) {
    return;
  }
  const promises = Array.from(entries.values()).map((entry) => entry.promise);
  await Promise.allSettled(promises);
};

export const isUserImageAttachment = (attachment: PromptAttachment): attachment is UserImageAttachment => {
  return attachment.type === PromptAttachmentType.IMAGE || attachment.type === PromptAttachmentType.CAPTURE_PHOTO || attachment.type === PromptAttachmentType.REFERENCE_IMAGE;
};

const isPendingCapableAttachment = (attachment: PromptAttachment): attachment is PendingCapableAttachment => {
  return isUserImageAttachment(attachment) || attachment.type === PromptAttachmentType.DOCUMENT;
};

export const isPendingUploadAttachment = (attachment: PromptAttachment): attachment is PendingUploadAttachment => {
  return isPendingCapableAttachment(attachment) && attachment.localId !== undefined && attachment.pendingUpload !== undefined;
};

export const getAttachmentPendingUpload = (attachment: PromptAttachment): PendingUpload | undefined => {
  return isPendingCapableAttachment(attachment) ? attachment.pendingUpload : undefined;
};

const isInFlightPendingUpload = (pendingUpload: PendingUpload | undefined): boolean => {
  return pendingUpload?.status === PendingUploadStatus.UPLOADING || pendingUpload?.status === PendingUploadStatus.PROCESSING;
};

export const getPendingUploadAttachments = (items: PromptAttachment[] | undefined): PendingUploadAttachment[] => {
  return (items ?? []).filter(isPendingUploadAttachment);
};

export const getInFlightPendingAttachments = (items: PromptAttachment[] | undefined): PendingUploadAttachment[] => {
  return getPendingUploadAttachments(items).filter((item) => isInFlightPendingUpload(item.pendingUpload));
};
